import { Box } from '@chakra-ui/react';
import { useIsMobile, useMelioIntl } from '@melio/ar-domain';
import {
  Container,
  ExternalLayout,
  Group,
  IconButton,
  LoadingContainer,
  NakedButton,
  Text,
  useToast,
} from '@melio/penny';
import { Bill, useBill, useFile, useVendor, Vendor } from '@melio/platform-api';
import { forwardRef } from '@melio/platform-utils';
import { useEffect, useState } from 'react';

import { Footer } from './Footer';
import { TempLogo } from './tempLogo';

export type GuestPaymentLayoutProps = {
  title?: string;
  isLoading?: boolean;
  billId?: string;
  hasPadding?: boolean;
  onBack?: VoidFunction;
  onClose?: VoidFunction;
  isDisabled?: boolean;
  isAuthenticating?: boolean;
  headerTextVariant?: HeaderTextVariant;
};

export const GuestPaymentLayout = forwardRef<GuestPaymentLayoutProps>(
  (
    {
      isAuthenticating,
      isLoading,
      billId,
      onBack,
      onClose,
      title,
      hasPadding = true,
      isDisabled,
      headerTextVariant = 'default',
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const { data: bill } = useBill({ id: billId });
    const { data: vendor } = useVendor({ id: bill?.vendorId });

    useEffect(() => {
      window.document.title = title ? `${title} | Melio` : 'Melio';
    }, [title]);

    return (
      <ExternalLayout
        data-layout={GuestPaymentLayout.displayName}
        isLoading={!vendor || !bill}
        content={
          <Container
            backgroundColor="white"
            border={isMobile ? 'none' : 'regular'}
            data-layout="GuestPaymentLayout.Container"
          >
            <HeaderSection
              bill={bill}
              vendor={vendor}
              onBack={onBack}
              onClose={onClose}
              isDisabled={isDisabled}
              variant={headerTextVariant}
            />
            <ContentSection title={title} isLoading={isLoading} hasPadding={hasPadding}>
              {children}
            </ContentSection>
          </Container>
        }
        footer={<Footer />}
        {...props}
        ref={ref}
      />
    );
  }
);

GuestPaymentLayout.displayName = 'GuestPaymentLayout';

type ContentSectionProps = Pick<GuestPaymentLayoutProps, 'title' | 'isLoading' | 'hasPadding'>;
const ContentSection = forwardRef<ContentSectionProps>(({ title, hasPadding, isLoading, children, ...props }, ref) => {
  const isMobile = useIsMobile();
  const defaultPaddingX = isMobile ? 'm' : 'l';
  return (
    <Container
      backgroundColor="white"
      paddingY="l"
      paddingX={hasPadding ? defaultPaddingX : 'none'}
      data-layout={ContentSection.displayName}
      {...props}
      ref={ref}
    >
      <Group variant="vertical" spacing="l">
        {title && (
          <Box paddingX={hasPadding ? (isMobile ? 'xs' : 'none') : defaultPaddingX}>
            <Text textStyle="heading3Semi">{title}</Text>
          </Box>
        )}
        <LoadingContainer isLoading={isLoading}>{children}</LoadingContainer>
      </Group>
    </Container>
  );
});
ContentSection.displayName = 'GuestPaymentLayout.ContentSection';

type HeaderTextVariant = 'default' | 'review-and-confirm';
type HeaderSectionProps = Pick<GuestPaymentLayoutProps, 'onBack' | 'onClose' | 'isDisabled'> & {
  bill?: Bill;
  vendor?: Vendor;
  variant: HeaderTextVariant;
};
const HeaderSection = forwardRef<HeaderSectionProps>(
  ({ bill, vendor, onBack, onClose, isDisabled, variant, ...props }, ref) => {
    const { formatCurrency, formatDate, formatMessage } = useMelioIntl();
    const downloadFile = useFileDownloader();

    const toast = useToast();
    const downloadInvoice = () => {
      downloadFile(bill?.invoice.fileId as string);
      toast({ title: formatMessage('ar.guestPayment.activities.paymentReview.toasts.download.text'), type: 'info' });
    };

    const HeaderText = () => {
      switch (variant) {
        case 'default': {
          return <Text textStyle="heading3Semi">{vendor?.name} has sent you an invoice for</Text>;
        }
        case 'review-and-confirm': {
          return <Text textStyle="heading3Semi">Review payment details</Text>;
        }
      }
    };

    return (
      <Container
        backgroundColor="light"
        paddingY="l"
        paddingX="l"
        data-layout={HeaderSection.displayName}
        {...props}
        ref={ref}
      >
        <Group variant="vertical" spacing="m">
          <Group variant="vertical" spacing="s">
            <Group variant="horizontal" spacing="s" justifyContent="space-between">
              {onBack ? (
                <IconButton
                  size="large"
                  variant="primary"
                  icon="arrow-left"
                  onClick={onBack}
                  aria-label="Back"
                  data-testid="layout-back-button"
                  isDisabled={isDisabled}
                />
              ) : (
                <TempLogo />
              )}
              {onClose && (
                <IconButton
                  size="large"
                  variant="primary"
                  icon="close"
                  onClick={onClose}
                  aria-label="Close"
                  data-testid="layout-back-button"
                  isDisabled={isDisabled}
                />
              )}
            </Group>
            <Group variant="vertical" spacing="xs">
              <HeaderText />
              <Text textStyle="heading1Semi">{bill && formatCurrency(bill.amount, bill.currency)}</Text>
            </Group>
          </Group>
          <Group variant="vertical" spacing="xs">
            <Text textStyle="body4" color="neutral.darkest">
              <strong>{formatMessage('ar.guestPayment.activities.paymentReview.subTitle.invoiceDue.label')}:</strong>{' '}
              {bill && formatDate(bill.dueDate)}
            </Text>
            <Group variant="horizontal" spacing="s" justifyContent="flex-start">
              <Text textStyle="body4" color="neutral.darkest">
                <NakedButton
                  onClick={downloadInvoice}
                  variant="primary"
                  size="small"
                  label={formatMessage('ar.guestPayment.activities.paymentReview.buttons.download.text')}
                  isDisabled={isDisabled}
                />
              </Text>
            </Group>
          </Group>
        </Group>
      </Container>
    );
  }
);

const useFileDownloader = () => {
  const [fileId, setFileId] = useState<string>();
  const { data: file } = useFile({ id: fileId });

  useEffect(() => {
    if (file) {
      window.location.replace(file.url);
    }
  }, [file]);

  return setFileId;
};

HeaderSection.displayName = 'GuestPaymentLayout.HeaderSection';
